import React, {
  ChangeEventHandler,
  MouseEventHandler,
  useEffect,
  useState,
} from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import ReactHtmlParser from 'react-html-parser';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import ReactDOMServer from 'react-dom/server';
import { useDispatch, useSelector } from 'react-redux';

import { useIntl } from 'react-intl';
import LocationMarker from '../../../images/LocationPinCircle.svg';
import RichText from '../../UI/RichText';
import Image from '../../UI/Image/Image';
import useTmsApi from '../../../hooks/useTmsApi';
import checkIcon from '../../../images/RedCheckMark.png';
import { addToCart } from '../../../redux/reducers/cartReducer';

import SelectedCarrier from '../../elements/SelectedCarrier/SelectedCarrier';
import SelectedTradeIn from '../../elements/SelectedTradeIn/SelectedTradeIn';
import { cartItemsSelector } from '../../../redux/selectors/cartSelectors';
import { ValidationError } from '../../AccessoriesComponents/VariantDetails/styles';
import { DppSkuResponse, DppSkuResponseItem } from '../../../types';
import { ISelectedCarrier } from '../types';
import {
  Phase2Screen,
  BladeTitle,
  BladeSubtitle,
  DetailSummary,
  InstrText,
  FormTitle,
  SectionWrapper,
  AddToCartButton,
  AddressWrapper,
  MarkerWrapper,
  LocationSummaryWrapper,
  OptText,
  OptWrapper,
  ErrorMsg,
  Spinner,
  ButtonText,
  SpinnerWrapper,
  GridWrapper,
  LegalText,
  DppContainer,
  ImageAndHeadingContainer,
  DppLogoWrapper,
  DppHeadingWrapper,
  DppLabelsContainer,
  DppMonthlyPrice,
  DppBenefits,
  DppFooter,
  CheckBoxLabel,
  CheckBox,
  FocusWrapper,
  DppDisclaimer,
  ErrorWrapper,
} from './styles';

interface Props {
  handleSubmit: MouseEventHandler<HTMLButtonElement>;
  reserveData: Record<string, any>;
  chosenPhoneDetails: string;
  selectedCarrier?: ISelectedCarrier;
  personalDetails: Record<string, any>;
  selectedLocation: Record<string, any>;
  isCaptchaChecked: boolean;
  recaptchaRef: Record<string, any>;
  hasErrorCaptcha: boolean;
  captchaHandler: (...args: any[]) => any;
  optInStatus: boolean;
  optInHandler: ChangeEventHandler<HTMLInputElement>;
  hasErrorOptIn: boolean;
  spinnerStatus: boolean;
  buttonDisabled: boolean;
  phoneModel: string;
  selectedStorageSize: string;
  selectedColour: string;
  variantLabels: Record<string, any>;
  selectedSKU: string | null;
  dppSelected: boolean;
  setDppSelected: (...args: any[]) => any;
  setDppResponse: (...args: any[]) => any;
  dppResponse?: DppSkuResponseItem[];
  phone: Record<string, any>;
  setPhase: (...args: any[]) => any;
  reserveStatus?: string;
  showDpp?: boolean;
  deviceType: string;
  showDisclaimer?: boolean;
  isReserveAccessoriesFFEnabled: boolean;
}

// Elements to render on rich text
const legalOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <LegalText>{children}</LegalText>,
  },
};

const dppBenefitsOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <DppBenefits>{children}</DppBenefits>
    ),
  },
};
const dppFooterOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <DppFooter>{children}</DppFooter>,
    [INLINES.HYPERLINK]: (entry) => (
      <a href={entry?.data?.uri} target="_blank" rel="noopener noreferrer">
        {entry.content[0].value}
      </a>
    ),
  },
};
const dppDisclaimerOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <DppDisclaimer>{children}</DppDisclaimer>
    ),
    [INLINES.HYPERLINK]: (entry) => (
      <a href={entry?.data?.uri} target="_blank" rel="noopener noreferrer">
        {entry.content[0].value}
      </a>
    ),
  },
};

// eslint-disable-next-line complexity
const ReservePhase2 = ({
  reserveData,
  handleSubmit,
  chosenPhoneDetails,
  phoneModel,
  selectedStorageSize,
  selectedColour,
  personalDetails,
  selectedCarrier,
  variantLabels,
  isCaptchaChecked,
  recaptchaRef,
  hasErrorCaptcha,
  captchaHandler,
  optInStatus,
  optInHandler,
  hasErrorOptIn,
  spinnerStatus,
  buttonDisabled,
  selectedLocation,
  selectedSKU,
  dppSelected,
  setDppSelected,
  setDppResponse,
  dppResponse,
  phone,
  setPhase,
  reserveStatus = '',
  showDpp = true,
  deviceType,
  showDisclaimer = true,
  isReserveAccessoriesFFEnabled,
}: Props) => {
  const { locale } = useIntl();
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState(false);
  const cartItems = useSelector(cartItemsSelector);

  if (typeof window !== 'undefined') {
    (window as any).recaptchaOptions = {
      lang: locale === 'en' ? 'en' : 'fr',
      useRecaptchaNet: true,
      removeOnUnmount: true,
    };
  }

  // CREATE DATALAYER FOR GTM ANALYTICS
  useEffect(
    () => {
    // Create dataLayer object for GTM if it doesn't exist yet
      window.dataLayer = window.dataLayer || [];
      // Structure data to submit
      const phase2Data = {
        event: 'reservenow',
        reservePhase: 'phase 2 - detail summary',
        phoneSummary: chosenPhoneDetails,
        phoneModel,
        phoneColour: selectedColour,
        phoneStorage: selectedStorageSize,
        carrier: selectedCarrier?.carrierName,
        storeSummary: `${selectedLocation.commonInfo.address.line1}, ${selectedLocation.commonInfo.address.city}, 
        ${selectedLocation.commonInfo.address.province}, ${selectedLocation.commonInfo.address.postalCode}`,
      };
      window.dataLayer.push(phase2Data);

      /* eslint-disable */
    //check if hj function exists
    (window as any).hj =
      (window as any).hj ||
      function () {
        (hj.q = hj.q || []).push(arguments);
      };
    //hj('trigger', 'phone_reservation');
    /* eslint-enable */
    },
    [chosenPhoneDetails,
      phoneModel,
      selectedCarrier?.carrierName,
      selectedColour,
      selectedLocation.commonInfo.address.city,
      selectedLocation.commonInfo.address.line1,
      selectedLocation.commonInfo.address.postalCode,
      selectedLocation.commonInfo.address.province,
      selectedStorageSize,
    ],
  );

  // API call to get DPP data

  const [dppRequest, setDppRequest] = useState<Record<string, any> | null>(
    null,
  );
  const [dppapiError, setDppApiError] = useState(false);

  const { response, error } = useTmsApi<DppSkuResponse>(
    '/device-protection-plan/price',
    dppRequest,
  );

  useEffect(() => {
    setDppRequest({ request: [{ productSku: `${selectedSKU}` }] });
  }, [selectedSKU]);

  useEffect(() => {
    if (response) {
      setDppResponse(response.response);
    }
  }, [response, setDppResponse]);

  useEffect(() => {
    if (error) {
      setDppApiError(true);
    }
  }, [error]);

  // Reserve labels data
  const reserveLabelSection = reserveData.combinatorSectionItems.find(
    (item) => item.contentful_id === '6u91Fz1Z19SYjf6u7JBInT',
  );
  const legalText = reserveData.combinatorSectionItems.find((item) => item.contentful_id === '1584sVrzU82OTeVy5LM4sA');
  const reserveLabels = reserveLabelSection.formFields.reduce(
    /* eslint-disable */
    (obj, item) => ((obj[item.contentful_id] = item), obj),
    {}
  );
  const tradeInLabel = reserveLabels['6Bpk5n0tpakonoZLTHeX3Y'].displayLabel;

  /* eslint-enable */
  // Legal section
  const optInSection = reserveData.combinatorSectionItems.find((item) => item.contentful_id === 'iB2DhHffofpgQzK9JWmEF');

  // Dpp section

  const dppDisclaimer = reserveData.combinatorSectionItems.find(
    ({ contentful_id }) => contentful_id === 'BzhZPieHnqGf0CPDJKud8',
  );

  const dppContent = reserveData.combinatorSectionItems.find(
    ({ contentful_id }) => contentful_id === '6PWqKWgsEp01J13EDsQOOz',
  );

  const dppHeading = dppContent?.moduleHeading;
  const dppImage = dppContent?.moduleImage;
  const dppLabels = dppContent?.moduleBody?.combinatorSectionItems;
  const dppFooter = dppContent?.moduleFooter;

  let dppMonthlyPriceLabel;
  if (dppResponse) {
    dppMonthlyPriceLabel = ReactDOMServer.renderToString(
      <RichText document={dppLabels[2]?.contentBody} />,
    )
      .replace('{monthlyFee}', dppResponse[0]?.monthlyFee.toString())
      .replace('{processingFee}', dppResponse[0]?.replacementFee.toString());
  }

  // Get phobio trade-in details if they exist
  const phobioDetails = window && JSON.parse(window.sessionStorage.getItem('phobioDetails')!);

  // Elements to render on rich text
  const optInOptions = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => (
        <OptText as="label" htmlFor="optIn">
          {children}
        </OptText>
      ),
      [BLOCKS.HEADING_1]: (_, children) => <OptText>{children}</OptText>,
      [BLOCKS.HEADING_2]: (_, children) => <OptText>{children}</OptText>,
    },
  };

  const quantityLimited = reserveData.combinatorSectionItems.find(
    ({ contentful_id }) => contentful_id === '6u91Fz1Z19SYjf6u7JBInT',
  ).formFields[15];

  const { errorMessage: qtyLimitReachedError, fieldContent } = quantityLimited;

  const onReserve = () => {
    if (selectedSKU) {
      const selectedPhone = phone.phoneVariants.find(
        ({ sku, phoneCarrier }) => sku === selectedSKU
          && phoneCarrier.cmsName.toLowerCase()
            === selectedCarrier?.carrierName?.toLowerCase(),
      );

      const quantity = cartItems.find((item) => item.productSku === selectedSKU)?.quantity
        || 0;

      if (quantity < 5) {
        dispatch(
          addToCart({
            ...selectedPhone,
            deviceType,
            reserveStatus,
            tradeIn: phobioDetails,
            dpp:
              dppSelected && dppResponse != null
                ? { dppSelected, dppResponse: dppResponse[0] }
                : { dppSelected },
          }),
        );
        setPhase(3);
      } else {
        setErrorMessage(qtyLimitReachedError);
      }
    }
  };

  return (
    <Phase2Screen>
      <BladeTitle data-testid="blade-title">
        {ReactHtmlParser(reserveLabels['1L0aPJB8Ac37fGinGrzE2x'].displayLabel)}
      </BladeTitle>
      <br />

      {!isReserveAccessoriesFFEnabled && (
      <InstrText>
        {ReactHtmlParser(
          reserveLabels['3xQ9H8G3bSdqDVLyFQGWuW'].displayLabel,
        )}
      </InstrText>
      )}

      {/* PHONE DETAILS */}
      <SectionWrapper>
        <BladeSubtitle>
          {ReactHtmlParser(
            reserveLabels['7rG36p3ep1ewgsgqBT9QFX'].displayLabel,
          )}
        </BladeSubtitle>
        <DetailSummary>{chosenPhoneDetails}</DetailSummary>
      </SectionWrapper>
      <hr />
      <SelectedTradeIn label={tradeInLabel} />
      
      {!isReserveAccessoriesFFEnabled && (
      <>
        {/* PERSONAL DETAILS */}
        <SectionWrapper>
          <BladeSubtitle>
            {ReactHtmlParser(
              reserveLabels['7rBYPQppwxiGa1byQSEWOj'].displayLabel,
            )}
          </BladeSubtitle>
          <FormTitle>
            {ReactHtmlParser(
              reserveLabels['5l1pOVDCPJSZOInsLqBb4F'].displayLabel,
            )}
          </FormTitle>
          <DetailSummary>
            {`${personalDetails.firstName}${' '}
          ${personalDetails.lastName}`}
          </DetailSummary>
          <FormTitle>
            {ReactHtmlParser(
              reserveLabels['4c8nsZU528mPagy0mRee7w'].displayLabel,
            )}
          </FormTitle>
          <DetailSummary>{personalDetails.email}</DetailSummary>
          <FormTitle>
            {ReactHtmlParser(
              reserveLabels['7rG36p3ep1ewgsgqBT9QFX'].displayLabel,
            )}
          </FormTitle>
          <DetailSummary>{personalDetails.phone}</DetailSummary>
        </SectionWrapper>
        <hr />
      </>
      )}

      {/* CARRIER */}
      <SectionWrapper>
        <BladeSubtitle>
          {ReactHtmlParser(
            reserveLabels['4zzSZ9iS3nFPUPVe0KhvP9'].displayLabel,
          )}
        </BladeSubtitle>
        {selectedCarrier && (
          <SelectedCarrier
            carrier={selectedCarrier}
            variantLabels={variantLabels}
          />
        )}
        {showDisclaimer && (
          <div>
            <RichText
              document={legalText?.contentBody}
              options={legalOptions}
            />
          </div>
        )}
      </SectionWrapper>
      <hr />
      {/* LOCATION */}
      <SectionWrapper>
        <BladeSubtitle>
          {ReactHtmlParser(reserveLabels['9fCWfccSH0enMDj5f9pXB'].displayLabel)}
        </BladeSubtitle>
        <LocationSummaryWrapper>
          <MarkerWrapper>
            <img src={LocationMarker} alt="map marker icon" />
          </MarkerWrapper>
          <AddressWrapper>
            <DetailSummary>
              {`${selectedLocation.commonInfo.address.line1}, ${selectedLocation.commonInfo.address.city}, 
        ${selectedLocation.commonInfo.address.province} ${selectedLocation.commonInfo.address.postalCode}`}
            </DetailSummary>
          </AddressWrapper>
        </LocationSummaryWrapper>
      </SectionWrapper>
      <hr />

      {/* Dpp */}
      {Boolean(showDpp && dppResponse?.[0]?.monthlyFee && !dppapiError) && (
        <SectionWrapper section="dpp">
          <DppContainer>
            <ImageAndHeadingContainer locale={locale}>
              <DppLogoWrapper>
                <Image image={dppImage} />
              </DppLogoWrapper>
              <DppHeadingWrapper>{dppHeading}</DppHeadingWrapper>
            </ImageAndHeadingContainer>
            <DppLabelsContainer dppSelected={dppSelected}>
              <CheckBoxLabel htmlFor="checkbox_dpp">
                <input
                  type="checkbox"
                  id="checkbox_dpp"
                  onChange={() => setDppSelected(!dppSelected)}
                  checked={dppSelected}
                  aria-checked={dppSelected}
                  aria-label="dpp checkbox"
                />

                <FocusWrapper className="focusWrapper">
                  <CheckBox
                    className={`customCheckbox ${dppSelected && 'checked'}`}
                    checked={dppSelected}
                  >
                    {dppSelected && <img src={checkIcon} alt="" />}
                  </CheckBox>
                </FocusWrapper>
              </CheckBoxLabel>

              <DppMonthlyPrice>
                {ReactHtmlParser(dppMonthlyPriceLabel)}
              </DppMonthlyPrice>

              <RichText
                document={dppLabels[3]?.contentBody}
                options={dppBenefitsOptions}
              />
            </DppLabelsContainer>
            {dppFooter && (
              <RichText document={dppFooter} options={dppFooterOptions} />
            )}
          </DppContainer>
        </SectionWrapper>
      )}

      {/* Dpp Disclaimer */}
      {!!(showDpp && dppDisclaimer && dppResponse?.[0]?.monthlyFee && !dppapiError) && (
        <RichText
          document={dppDisclaimer?.contentBody}
          options={dppDisclaimerOptions}
        />
      )}
      {!isReserveAccessoriesFFEnabled && (
      <>
        {/* OPT IN */}
        <SectionWrapper>
          <OptWrapper>
            <input
              type="checkbox"
              id="optIn"
              onChange={optInHandler}
              checked={optInStatus}
              data-testid="reserve-phase-2-optin"
            />
            <RichText
              document={optInSection.subHeading}
              options={optInOptions}
            />
          </OptWrapper>
          {hasErrorOptIn && (
          <ErrorMsg className="optInError">
            &#x2718;
            {' '}
            <RichText
              document={optInSection.contentBody}
              options={optInOptions}
            />
          </ErrorMsg>
          )}
        </SectionWrapper>
        <br />
      </>
      )}
      {/* CAPTCHA */}
      {!isReserveAccessoriesFFEnabled && (

      <SectionWrapper id="captchaWrapper">
        <ReCAPTCHA
          id="captcha"
          ref={recaptchaRef}
          lang={locale === 'en' ? 'en' : 'fr'}
          sitekey={process.env.GATSBY_RECAPTCHA_KEY}
          onChange={() => {
            captchaHandler(true);
          }}
          onExpired={() => {
            captchaHandler(false);
          }}
          name="isCaptcha"
          value={isCaptchaChecked}
          data-testid="recaptcha"
        />
        {hasErrorCaptcha && (
        <ErrorMsg className="recaptcha">
          &#x2718;
          {' '}
          {ReactHtmlParser(
            reserveLabels['7ofFTNDfXKMiW7O7h4zoaH'].errorMessage,
          )}
        </ErrorMsg>
        )}
      </SectionWrapper>
      )}

      {/* PROGRESS TO NEXT SCREEN */}
      <SectionWrapper id="reserveButton">
        {isReserveAccessoriesFFEnabled ? (
          <>
            {' '}
            <AddToCartButton
              type="button"
              onClick={() => onReserve()}
              disabled={buttonDisabled}
              data-ga="cta-add to basket 2-phones"
            >
              <GridWrapper>
                <ButtonText>
                  {ReactHtmlParser(
                    reserveLabels['2HbE76jxj9OufHvu4vdz6n'].displayLabel,
                  )}
                </ButtonText>
                <SpinnerWrapper>{spinnerStatus && <Spinner />}</SpinnerWrapper>
              </GridWrapper>
            </AddToCartButton>
            {errorMessage && (
            <ErrorWrapper>
              <Image image={fieldContent[0]} />
              <ValidationError>{errorMessage}</ValidationError>
            </ErrorWrapper>
            )}
          </>
        ) : (
          <AddToCartButton
            type="button"
            onClick={handleSubmit}
            disabled={buttonDisabled}
          >
            <GridWrapper>
              <ButtonText>
                {ReactHtmlParser(
                  reserveLabels['2k3Hx7EHbB2Gs8Ikinilpm'].displayLabel,
                )}
              </ButtonText>
              <SpinnerWrapper>{spinnerStatus && <Spinner />}</SpinnerWrapper>
            </GridWrapper>
          </AddToCartButton>
        )}

      </SectionWrapper>
    </Phase2Screen>
  );
};

export default ReservePhase2;
